import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { InventoryItem, InventoryItemApp } from '../common/inventory-item';
import { ItemConflictService } from '../services/item-conflict.service';
import { InventoryDependencyItem, ItemDependencyService } from '../services/item-dependency.service';
import { SelectedItem } from './interface';
import { convertInventoryItemToSelectedItem, findItem } from './util/item';

export interface SelectionOptions {
  partnerId: string;
  marketId: string;
  confirmItenComflictResolutionCallback?: () => Observable<boolean>;
  enforceDependencies?: boolean;
}

type SelectionEventType = 'selected' | 'deselected';

type SelectionEventSource =
  | 'user'
  | 'conflict-duplicate-item'
  | 'conflict-intersecting-items'
  | 'dependency-selection-change'
  | 'dependency-edition-change'
  | 'dependency-unavailable';

export interface SelectionEvent {
  triggerItem?: SelectedItem;
  updatedItems: SelectedItem[];
  eventType: SelectionEventType;
  eventSource: SelectionEventSource;
}

@Injectable()
export class ItemSelectorServiceV2 {
  private _selectedItems: WritableSignal<SelectedItem[]> = signal([]);
  private _immutableSelectedItems: WritableSignal<SelectedItem[]> = signal([]);
  private _availableItems: WritableSignal<InventoryItem[]> = signal([]);
  private _changeEvents: WritableSignal<SelectionEvent[]> = signal([]);

  public readonly selectedItems: Signal<SelectedItem[]> = this._selectedItems.asReadonly();
  public readonly immutableSelectedItems: Signal<SelectedItem[]> = this._immutableSelectedItems.asReadonly();
  public readonly availableItems: Signal<SelectedItem[]> = this._availableItems.asReadonly();
  public readonly changeEvents: Signal<SelectionEvent[]> = this._changeEvents.asReadonly();

  public readonly allItems: Signal<SelectedItem[]> = computed(() => {
    const selectedItems = this._selectedItems();
    const immutableSelectedItems = this._immutableSelectedItems();
    return [...selectedItems, ...immutableSelectedItems];
  });

  constructor(
    private itemConflictService: ItemConflictService,
    private itemDependencyService: ItemDependencyService,
  ) {}

  public initSelectedItems(value: SelectedItem[]) {
    this._selectedItems.set(value ?? []);
    // For now, reset the change events when the items are selected to deal
    // with the circular dependency between the item selector and the activation
    // service causing items to be re-selected from a different "selection session"
    // for a different account.
    this._changeEvents.set([]);
  }

  public initImmutableSelectedItems(items: SelectedItem[]) {
    const immutableItems = items?.map<SelectedItem>((i) => ({ ...i, immutable: true }));
    this._immutableSelectedItems.set(immutableItems ?? []);
  }

  // availableItems can be re-selected and are taken into consideration for dependency resolution
  public initAvailableItems(items: SelectedItem[]) {
    this._availableItems.set(items ?? []);
  }

  public updateSelection(isSelected: boolean, targetItem: SelectedItem, options?: SelectionOptions): void {
    if (isSelected) {
      this.addSelection(targetItem, 'user', options);
      return;
    }

    const [, index] = findItem(this.selectedItems(), targetItem.itemId);
    if (isIndexValid(index)) {
      this.removeSelection(index, 'user', options);
    }
  }

  public updateItemEdition(targetItem: SelectedItem, options?: SelectionOptions): void {
    const [, index] = findItem(this.selectedItems(), targetItem.itemId);

    if (!isIndexValid(index)) {
      if (targetItem.immutable) {
        this.updateSelection(
          true,
          {
            ...targetItem,
            quantity: targetItem?.quantity || 1,
          },
          options,
        );
      }
      return;
    }

    this._selectedItems.update((selectedItems) => {
      const selectedItem = selectedItems[index];
      if (selectedItem.type === 'app') {
        const targetApp = targetItem as InventoryItemApp;
        selectedItem.edition = targetApp.edition;
      }
      return [...selectedItems];
    });
    const updatedItem = this.selectedItems()[index];
    this.pushSelectionEvent([updatedItem], 'selected', 'user');
    if (updatedItem.type === 'package' || !options?.enforceDependencies) {
      return;
    }

    const selectedItems = this.selectedItems();
    this.removeBrokenDependencies(
      options.partnerId,
      options.marketId,
      selectedItems,
      'dependency-edition-change',
      updatedItem,
    );
  }

  public updateItemQuantity(targetItem: SelectedItem, options?: SelectionOptions): void {
    const [, index] = findItem(this.selectedItems(), targetItem.itemId);
    const quantity = targetItem.quantity;

    // add item with quantity
    if (!isIndexValid(index)) {
      this.addSelection(targetItem, 'user', options);
      return;
    }

    // update item quantity
    if (quantity > 0) {
      this._selectedItems.update((selectedItems) => {
        selectedItems[index].quantity = quantity;
        return [...selectedItems];
      });
      const updatedItem = this.selectedItems()[index];
      this.pushSelectionEvent([updatedItem], 'selected', 'user');
      return;
    }

    // remove selected item
    if (quantity <= 0) {
      this.removeSelection(index, 'user', options);
    }
  }

  public removeItemId(itemId: string, eventSource: SelectionEventSource, options?: SelectionOptions): void {
    const [, index] = findItem(this.selectedItems(), itemId);
    if (isIndexValid(index)) {
      this.removeSelection(index, eventSource, options);
    }
  }

  public clearSelection(clearedByUser = false): void {
    if (clearedByUser) {
      this.pushSelectionEvent([...this.selectedItems()], 'deselected', 'user');
    }
    this._selectedItems.set([]);
  }

  private addSelection(
    selectedItem: SelectedItem,
    eventSource: SelectionEventSource,
    options?: SelectionOptions,
  ): void {
    const selectedItems = this.selectedItems();
    const immutableSelectedItems = this.immutableSelectedItems();
    const availableItems = this.availableItems();
    this._selectedItems.update((selectedItems) => [...selectedItems, selectedItem]);
    this.pushSelectionEvent([selectedItem], 'selected', eventSource);
    this.validateItemSelections(selectedItem, selectedItems, immutableSelectedItems, availableItems, options);
  }

  private removeSelection(itemIndex: number, eventSource: SelectionEventSource, options?: SelectionOptions): void {
    const selectedItems = this._selectedItems();
    const [removedItem] = selectedItems.splice(itemIndex, 1);
    this._selectedItems.set([...selectedItems]);
    this.pushSelectionEvent([removedItem], 'deselected', eventSource);

    if (removedItem.type === 'package' || !options?.enforceDependencies) {
      return;
    }

    this.removeBrokenDependencies(
      options.partnerId,
      options.marketId,
      selectedItems,
      'dependency-selection-change',
      removedItem,
    );
  }

  private validateItemSelections(
    selectedItem: SelectedItem,
    selectedItems: SelectedItem[],
    immutableSelectedItems: SelectedItem[],
    availableItems: InventoryItem[],
    options?: SelectionOptions,
  ): void {
    // When resolving item conflicts, only consider the selected items so multi-activatable items
    // can be selected again, even if there are active.
    const items = [...selectedItems, ...immutableSelectedItems, ...availableItems];
    const resolvedItems$ = this.resolveItemConflicts(selectedItem, selectedItems, options);
    const missingItems$ = resolvedItems$.pipe(
      switchMap((resolvedItems) => {
        const updatedItems = this.replaceSelectedItemWithResolvedItems(selectedItem, selectedItems, resolvedItems);
        return this.findMissingDependencies(updatedItems, items, options);
      }),
    );
    // TODO - refactor this when the item selector service supports selecting multiple items simultaneously
    // because not doing this causes a flicker when a package is decomposed into its contents
    resolvedItems$.pipe(take(1)).subscribe((resolvedItems) => this.handleItemConflicts(selectedItem, resolvedItems));
    if (!options?.enforceDependencies) {
      return;
    }
    missingItems$
      .pipe(take(1))
      .subscribe((missingItems) => this.handleMissingDependencies(selectedItem, missingItems, options));
  }

  private resolveItemConflicts(
    selectedItem: SelectedItem,
    availableItems: SelectedItem[],
    options?: SelectionOptions,
  ): Observable<SelectedItem[]> {
    const resolvedItems$ = this.itemConflictService
      .resolveItemConflicts(selectedItem, availableItems, options?.partnerId ?? '', options?.marketId ?? '')
      .pipe(
        map((resolvedItems) => resolvedItems.map((ri) => convertInventoryItemToSelectedItem(ri, false))),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    return resolvedItems$.pipe(
      switchMap((resolvedItems) => {
        const shouldConfirm = resolvedItems?.length && resolvedItems[0].itemId !== selectedItem.itemId;
        if (!shouldConfirm || !options?.confirmItenComflictResolutionCallback) {
          return of(true);
        }
        return options.confirmItenComflictResolutionCallback();
      }),
      withLatestFrom(resolvedItems$),
      map(([confirmation, resolvedItems]) => {
        if (!confirmation) {
          return [];
        }
        return resolvedItems ?? [];
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private handleItemConflicts(selectedItem: SelectedItem, resolvedItems: SelectedItem[]): void {
    if (resolvedItems?.length === 1 && resolvedItems[0].itemId === selectedItem.itemId) {
      // In this case, there are no conflicts and the item is already selected.
      return;
    }

    this._selectedItems.update((selectedItems) => [
      ...this.replaceSelectedItemWithResolvedItems(selectedItem, selectedItems, resolvedItems),
    ]);

    let updatedItems = [selectedItem];
    let eventType: SelectionEventType = 'deselected';
    let eventSource: SelectionEventSource = 'conflict-duplicate-item';
    if (resolvedItems?.length) {
      updatedItems = resolvedItems;
      eventType = 'selected';
      eventSource = 'conflict-intersecting-items';
    }
    this.pushSelectionEvent(updatedItems, eventType, eventSource);
  }

  private replaceSelectedItemWithResolvedItems(
    selectedItem: SelectedItem,
    selectedItems: SelectedItem[],
    resolvedItems: SelectedItem[],
  ): SelectedItem[] {
    const filteredItems = selectedItems.filter((si) => si.itemId !== selectedItem.itemId);
    return [...filteredItems, ...resolvedItems];
  }

  private findMissingDependencies(
    targetItems: SelectedItem[],
    availableItems: SelectedItem[],
    options?: SelectionOptions,
  ): Observable<Map<string, InventoryDependencyItem[]>> {
    return this.itemDependencyService
      .listMissingRequirementsForItems(options?.partnerId ?? '', options?.marketId ?? '', targetItems, availableItems)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  private handleMissingDependencies(
    triggerItem: SelectedItem,
    missingItemsMap: Map<string, InventoryDependencyItem[]>,
    options?: SelectionOptions,
  ): void {
    missingItemsMap.forEach((missingItems, targetItemId) => {
      if (missingItems.some((mi) => !mi.enabled)) {
        this.removeItemId(targetItemId, 'dependency-unavailable', {
          partnerId: options?.partnerId ?? '',
          marketId: options?.marketId ?? '',
          enforceDependencies: false,
        });
        return;
      }

      const requiredItems = missingItems.map((mi) => convertInventoryItemToSelectedItem(mi, false));
      this._selectedItems.update((selectedItems) => {
        requiredItems.forEach((requiredItem) => {
          const [targetItem, targetItemIdx] = findItem(selectedItems, requiredItem.itemId);
          if (!isIndexValid(targetItemIdx)) {
            selectedItems.push(requiredItem);
          } else {
            if (targetItem?.type === 'app' && requiredItem?.type === 'app') {
              const targetApp = targetItem as InventoryItemApp;
              const requiredApp = requiredItem as InventoryItemApp;
              targetApp.edition = requiredApp.edition;
            }
          }
        });
        return [...selectedItems];
      });
      this.pushSelectionEvent(requiredItems, 'selected', 'dependency-selection-change', triggerItem);
    });
  }

  private removeBrokenDependencies(
    partnerId: string,
    marketId: string,
    targetItems: SelectedItem[],
    eventSource: SelectionEventSource,
    triggerItem?: SelectedItem,
  ): void {
    const selectedItems = this.selectedItems();
    // TODO - replace this with active items?
    const activeItems = this.immutableSelectedItems();
    const availabeItems = selectedItems.concat(activeItems);
    this.itemDependencyService
      .listMissingRequirementsForItems(partnerId, marketId, targetItems, availabeItems)
      .pipe(
        take(1),
        map((missingItemsMap) => Array.from(missingItemsMap.keys())),
      )
      .subscribe((brokenItemIds) => {
        if (!brokenItemIds?.length) {
          return;
        }

        const selectedItems = this._selectedItems();
        const removedItems: SelectedItem[] = [];
        brokenItemIds.forEach((brokenItemId) => {
          const [, brokenItemIdx] = findItem(selectedItems, brokenItemId);
          const [removedItem] = selectedItems.splice(brokenItemIdx, 1);
          removedItems.push(removedItem);
        });
        this._selectedItems.set([...selectedItems]);
        this.pushSelectionEvent(removedItems, 'deselected', eventSource, triggerItem);
      });
  }

  private pushSelectionEvent(
    updatedItems: SelectedItem[],
    eventType: SelectionEventType,
    eventSource: SelectionEventSource,
    triggerItem?: SelectedItem,
  ) {
    this._changeEvents.update((changeEvents) => {
      return [{ triggerItem, updatedItems, eventType, eventSource }, ...changeEvents];
    });
  }
}

function isIndexValid(index: number): boolean {
  return index >= 0;
}
